import {
  getConfusionTags,
  getSyllableSoundTags,
  getWordSoundTags
} from '@/lib/pronunciation/sounds'
import {
  pinyinNumberToDisplay,
  pinyinTextNumberToDisplay,
  pinyinTextNumberToZhuyin
} from '@/lib/pronunciation/pinyin'
import { generatedSyllableAudio } from './generated-syllables'
import { pronunciationLessons } from './lessons'
import { pronunciationWordSeeds } from './word-seeds'
import type {
  PronunciationItem,
  PronunciationLesson,
  PronunciationScript
} from './types'

const lessonById = new Map(
  pronunciationLessons.map((lesson) => [lesson.id, lesson])
)
const lessonOrderById = new Map(
  pronunciationLessons.map((lesson) => [lesson.id, lesson.order])
)
const syllableLessonIds = new Map<string, string[]>()

for (const lesson of pronunciationLessons) {
  for (const itemId of lesson.syllableItemIds) {
    const ids = syllableLessonIds.get(itemId) ?? []
    ids.push(lesson.id)
    syllableLessonIds.set(itemId, ids)
  }
}

const syllableItems: PronunciationItem[] = generatedSyllableAudio.map(
  (entry) => {
    const id = `syllable-${entry.id}`
    const soundTags = getSyllableSoundTags(entry.id)
    const lessonIds = syllableLessonIds.get(id) ?? []
    const level =
      lessonIds
        .map((lessonId) => lessonOrderById.get(lessonId) ?? 99)
        .sort((a, b) => a - b)[0] ?? 99

    return {
      id,
      type: 'syllable',
      pinyin: pinyinNumberToDisplay(entry.id),
      pinyinNumber: entry.id,
      zhuyin: pinyinTextNumberToZhuyin(entry.id),
      audioPath: entry.audioPath,
      lessonIds,
      soundTags,
      confusionTags: getConfusionTags(soundTags),
      level
    }
  }
)

const traditionalMap: Record<string, string> = {
  妈妈: '媽媽',
  吗: '嗎',
  马: '馬',
  米饭: '米飯',
  飞: '飛',
  门: '門',
  来: '來',
  老师: '老師',
  买: '買',
  卖: '賣',
  国家: '國家',
  学: '學',
  钱: '錢',
  谢谢: '謝謝',
  喜欢: '喜歡',
  请: '請',
  词: '詞',
  书: '書',
  热: '熱',
  这: '這',
  车: '車',
  汉字: '漢字',
  听: '聽',
  学生: '學生',
  汉语: '漢語',
  云: '雲',
  女儿: '女兒',
  银行: '銀行',
  远: '遠',
  美国: '美國',
  什么: '什麼',
  哪里: '哪裡',
  谁: '誰',
  再见: '再見',
  学校: '學校',
  学习: '學習',
  饭店: '飯店',
  电脑: '電腦',
  苹果: '蘋果',
  猫: '貓',
  天气: '天氣'
}

const emojiMap: Record<string, string> = {
  'word-mama': '👩',
  'word-baba': '👨',
  'word-ma-question': '❓',
  'word-horse': '🐎',
  'word-one': '1️⃣',
  'word-two': '2️⃣',
  'word-five': '5️⃣',
  'word-rice': '🍚',
  'word-touch': '👉',
  'word-eight': '8️⃣',
  'word-no': '❌',
  'word-friend': '🤝',
  'word-minute': '⏱️',
  'word-fly': '✈️',
  'word-door': '🚪',
  'word-ticket': '🎫',
  'word-you': '🫵',
  'word-he': '👨',
  'word-she': '👩',
  'word-come': '➡️',
  'word-teacher': '🧑‍🏫',
  'word-too': '📈',
  'word-all': '👥',
  'word-buy': '🛒',
  'word-sell': '🏷️',
  'word-tall': '📏',
  'word-older-brother': '👦',
  'word-can': '✅',
  'word-very': '👍',
  'word-good': '👌',
  'word-country': '🗺️',
  'word-work': '💼',
  'word-drink': '🥛',
  'word-day': '☀️',
  'word-text': '📄',
  'word-study-verb': '📖',
  'word-go': '🚶',
  'word-home': '🏠',
  'word-money': '💵',
  'word-small': '🔍',
  'word-thanks': '🙏',
  'word-call': '🗣️',
  'word-like': '❤️',
  'word-today': '📅',
  'word-please': '🙇',
  'word-seven': '7️⃣',
  'word-nine': '9️⃣',
  'word-at': '📍',
  'word-again': '🔁',
  'word-vegetable': '🥗',
  'word-three': '3️⃣',
  'word-four': '4️⃣',
  'word-word': '🔤',
  'word-early': '🌅',
  'word-sit': '🪑',
  'word-walk': '🚶',
  'word-character': '📝',
  'word-be': '⚖️',
  'word-person': '👤',
  'word-eat': '🍎',
  'word-book': '📚',
  'word-hot': '🥵',
  'word-this': '👇',
  'word-car': '🚗',
  'word-mountain': '⛰️',
  'word-up': '⬆️',
  'word-chinese-language': '🇨🇳',
  'word-hanzi': '✍️',
  'word-water': '💧',
  'word-think': '💭',
  'word-listen': '🎧',
  'word-name': '🏷️',
  'word-tomorrow': '🌅',
  'word-student': '🎒',
  'word-mandarin': '🗣️',
  'word-last-year': '📅',
  'word-cloud': '☁️',
  'word-daughter': '👧',
  'word-bank': '🏦',
  'word-moon': '🌙',
  'word-far': '🔭',
  'word-america': '🇺🇸',
  'word-what': '❓',
  'word-where': '📍',
  'word-who': '👤',
  'word-goodbye': '👋',
  'word-school': '🏫',
  'word-study': '📝',
  'word-restaurant': '🍽️',
  'word-computer': '💻',
  'word-apple': '🍎',
  'word-tea': '🍵',
  'word-coffee': '☕',
  'word-cat': '🐱',
  'word-dog': '🐶',
  'word-weather': '🌤️'
}

const wordItems: PronunciationItem[] = pronunciationWordSeeds.map((seed) => {
  const soundTags = getWordSoundTags(seed.pinyinNumber, seed.extraSoundTags)
  const confusionTags = Array.from(
    new Set([...(seed.confusionTags ?? []), ...getConfusionTags(soundTags)])
  ).sort()

  const traditional = traditionalMap[seed.hanzi] || seed.hanzi
  const emoji = emojiMap[seed.id] || '📝'

  return {
    id: seed.id,
    type: 'word',
    hanzi: seed.hanzi,
    traditional,
    emoji,
    meaning: seed.meaning,
    pinyin: pinyinTextNumberToDisplay(seed.pinyinNumber),
    pinyinNumber: seed.pinyinNumber,
    zhuyin: pinyinTextNumberToZhuyin(seed.pinyinNumber),
    audioPath: seed.audioPath ?? `/audio/cmn/64k/hsk/cmn-${seed.hanzi}.mp3`,
    lessonIds: seed.lessonIds,
    soundTags,
    confusionTags,
    level: seed.level
  }
})

const itemById = new Map<string, PronunciationItem>()
for (const item of [...syllableItems, ...wordItems]) itemById.set(item.id, item)

export function getLessons(): PronunciationLesson[] {
  return [...pronunciationLessons].sort((a, b) => a.order - b.order)
}

export function getLessonById(id: string) {
  return lessonById.get(id) ?? null
}

export function getSyllableItems() {
  return syllableItems
}

export function getWordItems() {
  return wordItems
}

export function getPronunciationItem(id: string) {
  return itemById.get(id) ?? null
}

export function getItemsByIds(ids: string[]) {
  return ids.flatMap((id) => {
    const item = itemById.get(id)
    return item ? [item] : []
  })
}

export function getItemsForLesson(lessonId: string) {
  const lesson = getLessonById(lessonId)
  if (!lesson) return { syllables: [], words: [] }

  return {
    syllables: getItemsByIds(lesson.syllableItemIds),
    words: getItemsByIds(lesson.wordItemIds)
  }
}

export function getPracticeWordsForLesson(lessonId: string) {
  return wordItems.filter((item) => item.lessonIds.includes(lessonId))
}

export function getConfusionBattleItems(confusionTag: string) {
  return [...syllableItems, ...wordItems].filter((item) =>
    item.confusionTags?.includes(confusionTag)
  )
}

export function getDisplayValue(
  item: Pick<PronunciationItem, 'pinyin' | 'zhuyin'>,
  script: PronunciationScript
) {
  return script === 'pinyin' ? item.pinyin : item.zhuyin
}

export function getSecondaryScript(
  script: PronunciationScript
): PronunciationScript {
  return script === 'pinyin' ? 'zhuyin' : 'pinyin'
}

export function getAllPronunciationItems() {
  return [...syllableItems, ...wordItems]
}

export { pronunciationLessons, pronunciationWordSeeds }
export type {
  PronunciationItem,
  PronunciationLesson,
  PronunciationProgress,
  PronunciationScript
} from './types'
